// CRUD de propiedades en Supabase con soporte para property_metadata JSONB

import { supabase } from '../../supabaseClient';
import { migrateBlindOffersToBids } from './openAuctionService';
import { recalculateAllFitsForProperty } from '../propertyFit/calculateBuyerFit';
import { DEFAULT_SEMAPHORE_THRESHOLDS } from '../../config/blindBiddingConfig';

/**
 * Obtiene todas las propiedades activas ordenadas por fecha de creación
 * @returns {Promise<Array>} Lista de propiedades activas en formato camelCase
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getProperties = async () => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false});

    if (error) throw error;

    return data.map(mapPropertyData);
  } catch (error) {
    console.error('Error obteniendo propiedades:', error);
    throw error;
  }
};

/**
 * Obtiene solo las propiedades archivadas (is_active = false)
 * Usado en AdminDashboard cuando showArchived=true
 * @returns {Promise<Array>} Lista de propiedades archivadas en formato camelCase
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getArchivedProperties = async () => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('is_active', false)
      .order('created_at', { ascending: false});

    if (error) throw error;

    return data.map(mapPropertyData);
  } catch (error) {
    console.error('Error obteniendo propiedades archivadas:', error);
    throw error;
  }
};

/**
 * Obtiene una propiedad específica por ID (solo activas)
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Object|null>} Propiedad en formato camelCase o null si no existe
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getProperty = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('id', propertyId)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error obteniendo propiedad:', error);
    throw error;
  }
};

/**
 * Obtiene una propiedad específica por ID (activa o archivada)
 * Usado en analytics y admin para acceder a propiedades archivadas
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Object|null>} Propiedad en formato camelCase o null si no existe
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getPropertyById = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('id', propertyId)
      .single();

    if (error) throw error;
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error obteniendo propiedad por ID:', error);
    throw error;
  }
};

/**
 * Transforma datos de base de datos (snake_case) a formato frontend (camelCase)
 * Convierte tipos de datos y aplica valores por defecto
 * @param {Object} data - Objeto de propiedad desde Supabase
 * @returns {Object|null} Propiedad en formato camelCase o null
 * @private
 */
const mapPropertyData = (data) => {
  if (!data) return null;

  return {
    id: data.id,
    title: data.title,
    description: data.description,
    videoUrl: data.video_url,
    floorPlanUrl: data.floor_plan_url,
    spectatorToken: data.spectator_token,
    photoUrls: data.photo_urls || [],
    startPrice: parseFloat(data.start_price),
    originalStartPrice: data.original_start_price ? parseFloat(data.original_start_price) : null,
    minAbsolute: parseFloat(data.min_absolute),
    minIncrement: parseFloat(data.min_increment),
    maxBidPercentage: parseFloat(data.max_bid_percentage),
    biddingStartDate: data.bidding_start_date,
    deadline: data.deadline,
    createdAt: data.created_at,
    isActive: data.is_active,
    adminEmails: data.admin_emails || [],
    last_chance_started_at: data.last_chance_started_at,
    last_chance_duration: data.last_chance_duration || 48, // Deprecated: usar last_chance_deadline
    last_chance_deadline: data.last_chance_deadline,
    last_chance_closed_at: data.last_chance_closed_at,
    original_deadline: data.original_deadline,
    calendlyUrl: data.calendly_url || '',
    propertyMetadata: data.property_metadata || {},
    documentUrls: data.document_urls || [],
    translationsEnabled: data.translations_enabled || false,
    // Campos para fase de oferta ciega
    blind_bidding_deadline: data.blind_bidding_deadline,
    blind_bidding_started_at: data.blind_bidding_started_at,
    blind_bidding_duration: data.blind_bidding_duration || 72,
    blind_bidding_closed_at: data.blind_bidding_closed_at,
    semaphore_green_threshold: data.semaphore_green_threshold ?? DEFAULT_SEMAPHORE_THRESHOLDS.green,
    semaphore_yellow_threshold: data.semaphore_yellow_threshold ?? DEFAULT_SEMAPHORE_THRESHOLDS.yellow,
  };
};

/**
 * Crea una nueva propiedad en estado UPCOMING
 * @param {Object} propertyData - Datos de la propiedad en formato camelCase
 * @param {string} propertyData.title - Título de la propiedad
 * @param {string} propertyData.description - Descripción detallada
 * @param {number} propertyData.startPrice - Precio inicial de salida
 * @param {number} propertyData.minAbsolute - Precio mínimo absoluto
 * @param {Array<string>} [propertyData.photoUrls] - URLs de fotos
 * @param {Array<string>} [propertyData.adminEmails] - Emails de admins a notificar
 * @returns {Promise<Object>} Propiedad creada en formato camelCase
 * @throws {Error} Si hay error al crear en Supabase
 */
export const createProperty = async (propertyData) => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .insert([
        {
          title: propertyData.title,
          description: propertyData.description,
          video_url: propertyData.videoUrl,
          floor_plan_url: propertyData.floorPlanUrl || null,
          photo_urls: propertyData.photoUrls || [],
          start_price: propertyData.startPrice,
          original_start_price: propertyData.startPrice, // Precio original de referencia
          min_absolute: propertyData.minAbsolute,
          min_increment: propertyData.minIncrement || 0, // Se configura al abrir subasta abierta
          max_bid_percentage: propertyData.maxBidPercentage || 0, // Se configura al abrir subasta abierta
          bidding_start_date: propertyData.biddingStartDate || null, // Se configura al abrir subasta abierta
          deadline: propertyData.deadline || null, // Se configura al abrir subasta abierta
          admin_emails: propertyData.adminEmails || [],
          last_chance_duration: propertyData.lastChanceDuration || 48,
          calendly_url: propertyData.calendlyUrl || null,
          property_metadata: propertyData.propertyMetadata || {},
          document_urls: propertyData.documentUrls || [],
          translations_enabled: propertyData.translationsEnabled || false,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error creando propiedad:', error);
    throw error;
  }
};

/**
 * Actualiza una propiedad existente y recalcula fits de compradores
 * @param {string} propertyId - UUID de la propiedad
 * @param {Object} propertyData - Datos actualizados en formato camelCase
 * @returns {Promise<Object>} Propiedad actualizada
 * @throws {Error} Si hay error al actualizar en Supabase
 */
export const updateProperty = async (propertyId, propertyData) => {
  try {
    const updateData = {
      title: propertyData.title,
      description: propertyData.description,
      is_active: propertyData.isActive,
      video_url: propertyData.videoUrl,
      floor_plan_url: propertyData.floorPlanUrl || null,
      photo_urls: propertyData.photoUrls || [],
      start_price: propertyData.startPrice,
      original_start_price: propertyData.startPrice, // Se actualiza junto con start_price mientras sea editable
      min_absolute: propertyData.minAbsolute,
      min_increment: propertyData.minIncrement,
      max_bid_percentage: propertyData.maxBidPercentage,
      bidding_start_date: propertyData.biddingStartDate,
      deadline: propertyData.deadline,
      admin_emails: propertyData.adminEmails || [],
      last_chance_duration: propertyData.lastChanceDuration || 48,
      calendly_url: propertyData.calendlyUrl || null,
      property_metadata: propertyData.propertyMetadata || {},
      document_urls: propertyData.documentUrls || [],
      translations_enabled: propertyData.translationsEnabled || false,
    };

    if (propertyData.lastChanceStartedAt !== undefined) {
      updateData.last_chance_started_at = propertyData.lastChanceStartedAt;
    }

    if (propertyData.last_chance_deadline !== undefined) {
      updateData.last_chance_deadline = propertyData.last_chance_deadline;
    }

    if (propertyData.lastChanceClosedAt !== undefined) {
      updateData.last_chance_closed_at = propertyData.lastChanceClosedAt;
    }

    const { data, error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', propertyId)
      .select()
      .single();

    if (error) throw error;

    // Recalcular fit para todos los compradores con perfil (async, no bloquea)
    recalculateAllFitsForProperty(propertyId)
      .then(result => {
        if (result.updated > 0) {
          console.log(`🔄 Fits recalculados: ${result.updated} perfiles actualizados`);
        }
      })
      .catch(err => console.error('Error recalculando fits:', err));

    return mapPropertyData(data);
  } catch (error) {
    console.error('Error actualizando propiedad:', error);
    throw error;
  }
};

/**
 * Archiva una propiedad (soft delete, marca is_active = false)
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<boolean>} true si se archivó correctamente
 * @throws {Error} Si hay error al archivar en Supabase
 */
export const deleteProperty = async (propertyId) => {
  try {
    const { error } = await supabase
      .from('properties')
      .update({ is_active: false })
      .eq('id', propertyId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error archivando propiedad:', error);
    throw error;
  }
};

/**
 * Reactiva una propiedad archivada (marca is_active = true)
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<boolean>} true si se reactivó correctamente
 * @throws {Error} Si hay error al reactivar en Supabase
 */
export const unarchiveProperty = async (propertyId) => {
  try {
    const { error } = await supabase
      .from('properties')
      .update({ is_active: true })
      .eq('id', propertyId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error reactivando propiedad:', error);
    throw error;
  }
};

/**
 * Extender deadline por anti-sniping
 * @param {string} propertyId - ID de la propiedad
 * @param {string} newDeadline - Nuevo deadline (ISO string)
 * @param {string|null} originalDeadline - Deadline original (solo se guarda la primera vez)
 */
export const extendDeadlineAntiSniping = async (propertyId, newDeadline, originalDeadline) => {
  try {
    const updateData = {
      deadline: newDeadline,
    };
    
    // Solo guardar original_deadline si se proporciona (primera extensión)
    if (originalDeadline) {
      updateData.original_deadline = originalDeadline;
    }

    const { data, error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', propertyId)
      .select()
      .single();

    if (error) throw error;
    
    console.log('⏰ Anti-sniping: Deadline extendido a', newDeadline);
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error extendiendo deadline (anti-sniping):', error);
    throw error;
  }
};

/**
 * Inicia la fase de oferta ciega (BLIND_WAITING → primera oferta activa contador)
 * Establece un deadline temporal de 1 año que se actualiza con primera oferta
 * @param {string} propertyId - UUID de la propiedad
 * @param {number} [greenThreshold] - Umbral verde del semáforo (% por debajo, default: 3)
 * @param {number} [yellowThreshold] - Umbral amarillo del semáforo (% por debajo, default: 9)
 * @returns {Promise<Object>} Propiedad actualizada
 * @throws {Error} Si hay error al iniciar fase en Supabase
 * @see {blindBiddingConfig} Para valores por defecto de duración y umbrales
 */
export const startBlindBiddingPhase = async (propertyId, greenThreshold, yellowThreshold) => {
  try {
    // Establecer un deadline lejano temporal (se actualizará cuando llegue primera oferta)
    const tempDeadline = new Date();
    tempDeadline.setFullYear(tempDeadline.getFullYear() + 1); // 1 año en el futuro

    const updateData = {
      blind_bidding_deadline: tempDeadline.toISOString(),
    };

    // Añadir umbrales del semáforo si se proporcionan
    if (greenThreshold !== undefined) {
      updateData.semaphore_green_threshold = greenThreshold;
    }
    if (yellowThreshold !== undefined) {
      updateData.semaphore_yellow_threshold = yellowThreshold;
    }

    const { data, error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', propertyId)
      .select()
      .single();

    if (error) throw error;
    
    console.log('🟣 Fase BLIND_WAITING iniciada para propiedad:', propertyId);
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error iniciando fase de oferta ciega:', error);
    throw error;
  }
};

/**
 * Cierra la fase de oferta ciega sin marcar finalistas
 * Admin debe luego seleccionar finalistas y abrir subasta OPEN manualmente
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Object>} Propiedad actualizada con blind_bidding_closed_at
 * @throws {Error} Si hay error al cerrar fase en Supabase
 * @note Ya no marca finalistas automáticamente - eso se hace en startOpenBiddingPhase
 */
export const closeBlindBiddingPhase = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('properties')
      .update({
        blind_bidding_closed_at: new Date().toISOString(),
      })
      .eq('id', propertyId)
      .select()
      .single();

    if (error) throw error;

    console.log('🔴 Fase BLIND cerrada para propiedad:', propertyId);
    return mapPropertyData(data);
  } catch (error) {
    console.error('Error cerrando fase de oferta ciega:', error);
    throw error;
  }
};

/**
 * Inicia la fase de subasta abierta (OPEN) después de fase ciega
 * Marca finalistas, migra ofertas ciegas seleccionadas a bids y configura parámetros
 * @param {string} propertyId - UUID de la propiedad
 * @param {number} startingPrice - Precio inicial (típicamente mejor oferta ciega)
 * @param {string} biddingStartDate - Inicio de subasta (ISO string)
 * @param {string} deadline - Fin de subasta (ISO string)
 * @param {number} minIncrement - Incremento mínimo entre pujas (€)
 * @param {number} maxBidPercentage - Incremento máximo permitido por puja (%)
 * @param {Array<string>} [finalistInviteIds=[]] - IDs de invitados finalistas
 * @param {Array<Object>} [selectedOffers=[]] - Ofertas ciegas a migrar a bids
 * @returns {Promise<Object>} Propiedad actualizada
 * @throws {Error} Si hay error al iniciar fase o marcar finalistas
 */
export const startOpenBiddingPhase = async (propertyId, startingPrice, biddingStartDate, deadline, minIncrement, maxBidPercentage, finalistInviteIds = [], selectedOffers = []) => {
  try {
    console.log('🔍 startOpenBiddingPhase called with:', {
      propertyId,
      startingPrice,
      biddingStartDate,
      deadline,
      minIncrement,
      maxBidPercentage,
      finalistInviteIds,
      selectedOffersCount: selectedOffers.length
    });

    // Usar las columnas existentes: bidding_start_date y deadline
    // Nota: original_start_price ya se guardó al crear/editar la propiedad
    const { data, error } = await supabase
      .from('properties')
      .update({
        bidding_start_date: biddingStartDate, // Inicio de subasta abierta
        deadline: deadline, // Fin de subasta abierta
        start_price: startingPrice,
        min_increment: minIncrement,
        max_bid_percentage: maxBidPercentage,
      })
      .eq('id', propertyId)
      .select()
      .single();

    if (error) {
      console.error('❌ Supabase error updating property:', error);
      throw error;
    }

    console.log('✅ Property updated successfully');

    // Revocar acceso a los NO-finalistas (todos empiezan con has_access=true)
    if (finalistInviteIds.length > 0) {
      console.log('🔍 Revoking access for non-finalists, finalists:', finalistInviteIds);

      const { error: invitesError } = await supabase
        .from('invites')
        .update({ has_access: false })
        .eq('property_id', propertyId)
        .not('id', 'in', `(${finalistInviteIds.join(',')})`);

      if (invitesError) {
        console.error('❌ Supabase error revoking non-finalist access:', invitesError);
        throw invitesError;
      }

      console.log(`✅ Acceso revocado para no-finalistas de propiedad ${propertyId}`);
    }

    // Migrar ofertas ciegas seleccionadas a bids
    if (selectedOffers.length > 0) {
      console.log('🔄 Migrando ofertas ciegas a bids...');
      const migratedCount = await migrateBlindOffersToBids(selectedOffers, propertyId);
      console.log(`✅ ${migratedCount} ofertas ciegas migradas a bids`);
    }

    console.log('🟢 Fase OPEN iniciada para propiedad:', propertyId, 'Precio inicial:', startingPrice);
    return mapPropertyData(data);
  } catch (error) {
    console.error('❌ Error iniciando fase de subasta abierta:', error);
    console.error('❌ Error stack:', error.stack);
    throw error;
  }
};